import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import {
  normalizeNotificationTypes,
  useNotificationTypeListQuery,
} from '@/client/api/campaigns/campaigns.query';
import { useUpdateNotificationTypeMutation } from '@/client/api/campaigns/campaigns.mutation';
import {
  isTruthyFlag,
  NotificationTypeListParams,
} from '@/client/types/campaigns';
import { useListFilters } from '@/client/hooks/useListFilters';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';

const FlagToggle = ({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) => (
  <label className="inline-flex items-center gap-1.5 text-xs text-gray-700">
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="rounded border-gray-300"
    />
    {label}
  </label>
);

export default function CampaignNotificationTypesPanel({ canWrite }: { canWrite: boolean }) {
  const {
    searchTerm,
    debouncedSearchTerm,
    filters,
    handleSearch,
    handleFilterChange,
    resetFilters,
  } = useListFilters<{ category: string; active_only: string }>({
    initialFilters: { category: '', active_only: '' },
    initialLimit: 100,
    // Namespaced so this panel's URL state doesn't collide with sibling tabs.
    namespace: 'ntype',
  });

  const [busyId, setBusyId] = useState<number | null>(null);
  const update = useUpdateNotificationTypeMutation();

  const listParams: NotificationTypeListParams = useMemo(
    () => ({
      ...(filters.category.trim() ? { category: filters.category.trim() } : {}),
      ...(filters.active_only === '1' || filters.active_only === '0'
        ? { active_only: filters.active_only }
        : {}),
    }),
    [filters],
  );

  const { data, isLoading, isError } = useNotificationTypeListQuery(listParams);
  const types = normalizeNotificationTypes(data).filter((t) => {
    const q = debouncedSearchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      String(t.name_system || '').toLowerCase().includes(q) ||
      String(t.name || '').toLowerCase().includes(q) ||
      String(t.ntypid).includes(q)
    );
  });

  const patch = async (
    ntypid: number,
    body: Parameters<typeof update.mutateAsync>[0]['body'],
  ) => {
    if (!canWrite) return;
    setBusyId(ntypid);
    try {
      await update.mutateAsync({
        ntypid,
        body,
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success('Updated', { richColors: true });
    } catch (error: any) {
      toast.error(error?.message || 'Update failed', { richColors: true });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-white p-4 shadow">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search name / name_system / id…"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <input
            type="text"
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            placeholder="Category"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          <select
            value={filters.active_only}
            onChange={(e) => handleFilterChange('active_only', e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">Any status</option>
            <option value="1">Active only</option>
            <option value="0">Inactive only</option>
          </select>
          {(searchTerm || filters.category || filters.active_only) && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        {isLoading && (
          <div className="space-y-3 p-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {isError && (
          <p className="p-8 text-center text-sm text-red-600">Failed to load notification types.</p>
        )}
        {!isLoading && !isError && types.length === 0 && (
          <p className="p-8 text-center text-sm text-gray-500">No notification types found.</p>
        )}
        {!isLoading && types.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Category</th>
                  <th className="px-4 py-3 font-medium">Active</th>
                  <th className="px-4 py-3 font-medium">Allow</th>
                  <th className="px-4 py-3 font-medium">Defaults</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {types.map((t) => {
                  const busy = busyId === t.ntypid;
                  return (
                    <tr key={t.ntypid} className={busy ? 'opacity-60' : ''}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{t.name || t.name_system}</p>
                        <p className="font-mono text-xs text-gray-500">
                          #{t.ntypid} · {t.name_system}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{t.category || '—'}</td>
                      <td className="px-4 py-3">
                        <FlagToggle
                          checked={isTruthyFlag(t.active)}
                          disabled={!canWrite || busy}
                          label="On"
                          onChange={(v) => patch(t.ntypid, { active: v })}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-3">
                          <FlagToggle
                            checked={isTruthyFlag(t.allow_in_app)}
                            disabled={!canWrite || busy}
                            label="In-app"
                            onChange={(v) => patch(t.ntypid, { allow_in_app: v })}
                          />
                          <FlagToggle
                            checked={isTruthyFlag(t.allow_email)}
                            disabled={!canWrite || busy}
                            label="Email"
                            onChange={(v) => patch(t.ntypid, { allow_email: v })}
                          />
                          <FlagToggle
                            checked={isTruthyFlag(t.allow_sms)}
                            disabled={!canWrite || busy}
                            label="SMS"
                            onChange={(v) => patch(t.ntypid, { allow_sms: v })}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-3">
                          <FlagToggle
                            checked={isTruthyFlag(t.default_in_app)}
                            disabled={!canWrite || busy}
                            label="In-app"
                            onChange={(v) => patch(t.ntypid, { default_in_app: v })}
                          />
                          <FlagToggle
                            checked={isTruthyFlag(t.default_email)}
                            disabled={!canWrite || busy}
                            label="Email"
                            onChange={(v) => patch(t.ntypid, { default_email: v })}
                          />
                          <FlagToggle
                            checked={isTruthyFlag(t.default_sms)}
                            disabled={!canWrite || busy}
                            label="SMS"
                            onChange={(v) => patch(t.ntypid, { default_sms: v })}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
